'use client';

import { ConfigProvider, Flex, Input, Select, Space } from 'antd';
import 'bootstrap-icons/font/bootstrap-icons.min.css';
import React, { useEffect, useState } from 'react';

import Icon from '../icon/icon';
import { AppColors } from './../../theme';
import styles from './search-bar.style';

/**
 * @internal
 * Props para el componente interno SearchTypeSelect.
 */
type SearchTypeSelectProps = {
  idType: string;
  onIdTypeChange: (value: string) => void;
  idTypeOptions: { label: string; value: string }[];
  placeholder: string;
  hasError?: boolean;
};

/**
 * Props para el componente SearchBar.
 */
type SearchBarProps = {
  /**
   * El tipo de búsqueda actualmente seleccionado (p. Ej., 'factura', 'pickslip').
   */
  idType: string;
  /**
   * Una función de devolución de llamada que se ejecuta cuando cambia el tipo de búsqueda.
   *
   * @param {string} value - El nuevo tipo de búsqueda.
   */
  onIdTypeChange?: (value: string) => void;
  /**
   * El valor introducido en el campo de entrada de búsqueda.
   */
  idValue: string;
  /**
   * Una función de devolución de llamada que se ejecuta cuando cambia el valor en el campo de entrada de búsqueda.
   */
  onIdValueChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Una función de devolución de llamada que se ejecuta cuando se activa una búsqueda.
   */
  onSearch?: () => void;
  /**
   * Un array de opciones para el selector de tipo de búsqueda.
   */
  idTypeOptions?: { label: string; value: string }[];
  /**
   * El texto del marcador de posición para el campo de entrada de búsqueda.
   */
  placeholder: string;
  /**
   * Un mensaje de error para mostrar debajo de la barra de búsqueda.
   */
  error?: string;
  /**
   * Si es verdadero, la barra de búsqueda se mostrará en estado de error.
   *
   * @default false
   */
  hasError?: boolean;
  /**
   * Si es verdadero, el addon de la barra de búsqueda se ocultará.
   *
   * @default false
   */
  hideAddon?: boolean;
  /**
   * Contenido adicional para renderizar dentro de la barra de búsqueda.
   */
  children?: React.ReactNode;
  /**
   * Estilos para el campo de entrada de búsqueda.
   */
  inputStyle?: React.CSSProperties;
  /**
   * El ID del campo de entrada de búsqueda.
   */
  id?: string;
};

/**
 * Un componente interno que renderiza el menú desplegable para seleccionar el tipo de búsqueda.
 */
const SearchTypeSelect = ({
  idType,
  onIdTypeChange,
  idTypeOptions,
  placeholder,
  hasError
}: SearchTypeSelectProps) => {
  const [rotate, setRotate] = useState(0);

  return (
    <ConfigProvider
      theme={{
        components: {
          Select: {
            colorText: AppColors.VA.NEUTRALS._90_,
            colorTextPlaceholder: AppColors.VA.NEUTRALS._90_,
            hoverBorderColor: AppColors.VA.PRIMARY._50_,
            colorBorder: AppColors.VA.BACKGROUND._30_,
            activeOutlineColor: 'none',
            selectorBg: AppColors.VA.BACKGROUND._05_
          }
        }
      }}>
      <Select
        aria-label='Tipo de búsqueda'
        suffixIcon={
          <Icon
            className='bi-chevron-down'
            style={{
              ...styles.searchBar__icon,
              transform: `rotate(${rotate}deg)`
            }}
          />
        }
        onOpenChange={(open: boolean) => setRotate(open ? 180 : 0)}
        placeholder={placeholder}
        value={idType}
        onChange={onIdTypeChange}
        options={idTypeOptions}
        style={styles.searchBar__select}
        status={hasError ? 'error' : undefined}
      />
    </ConfigProvider>
  );
};

const SearchBar = ({
  idType,
  onIdTypeChange,
  idValue,
  onIdValueChange,
  onSearch,
  idTypeOptions,
  placeholder,
  error,
  hasError = false,
  hideAddon = false,
  children,
  inputStyle,
  id
}: SearchBarProps) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const addon = (error: boolean) => (
    <SearchTypeSelect
      idType={idType}
      onIdTypeChange={onIdTypeChange!}
      idTypeOptions={idTypeOptions!}
      placeholder={placeholder}
      hasError={error}
    />
  );
  return (
    <Flex vertical>
      <Space.Compact style={styles.searchBar} size='large'>
        {!hideAddon && addon(hasError)}
        <ConfigProvider
          theme={{
            components: {
              Input: {
                colorError: AppColors.VA.ERROR._50_,
                activeShadow: 'none',
                hoverBorderColor: AppColors.VA.PRIMARY._50_,
                colorBorder: AppColors.VA.BACKGROUND._30_
              }
            }
          }}>
          {isClient && (
            <Input
              id={id}
              allowClear
              aria-label='Valor a buscar'
              value={idValue}
              onChange={onIdValueChange}
              onPressEnter={(e) => {
                if (e.repeat) {
                  return;
                }
                e.stopPropagation();
                onSearch?.();
              }}
              placeholder={placeholder}
              style={{
                ...styles.searchBar__input,
                ...(hideAddon && styles.searchBar__fullInput),
                ...(hasError && styles.searchBar__errorStatus),
                ...inputStyle
              }}
            />
          )}
        </ConfigProvider>
        {children}
      </Space.Compact>

      {hasError && error && <div style={styles.searchBar__error}>{error}</div>}
    </Flex>
  );
};

export default SearchBar;
